<template>
  <PageSection disable-sticky-margin :title="$t('attributes.ceremony')">
    <BaseSimpleRadioGroup
      name="ceremony"
      default-value="false"
      :isShowingKey="false"
      :isReturningKey="true"
      :disabled="permission"
      :options="yes_no_options"
      translationExtention="enums.yes_no"
      @change.self="getCeremonyValue"
    />

    <div v-if="ceremony === 'true'" :key="ceremony" class="flex flex-col gap-4">
      <!--#region ask_nb_people_when_work_creating true -->
      <template v-if="settings?.events?.ask_nb_people_when_work_creating">
        <div class="max-w-xs">
          <BaseInput
            :label="$t('attributes.nb_peoples')"
            :placeholder="$t('attributes.nb_peoples')"
            name="nb_peoples"
            type="number"
            parseType="int"
            :min="0"
            :max="999999"
            v-model="eventCER.nb_peoples"
            :disabled="permission"
          />
        </div>
        <template v-if="eventCER.nb_peoples">
          <BaseRadioCards
            v-if="
              metadata &&
              (auth_role === 'crema-user' || auth_role === 'super-admin')
            "
            v-model="data_room"
            :label="$t('attributes.ceremony_place')"
            :options="qualifiedRoomOptions"
            :disabled="permission"
          />
          <BaseRadioCards
            v-if="auth_role === 'customer-user'"
            v-model="customer_user_room"
            :label="$t('global.room')"
            :options="toOptions(qualifiedRooms)"
            :disabled="permission"
          />
          <BaseRadioCards
            name="event_kind_id"
            :label="$t('attributes.ceremony_type')"
            :model-value="eventCER.event_kind_id"
            :options="ceremonyKindOptions"
            :disabled="permission"
            @change="getInputValue"
          />
        </template>
        <div v-if="need_more_infos">
          <BaseInput
            :label="$t('attributes.funeral_date_and_place')"
            :placeholder="$t('attributes.funeral_date_and_place')"
            name="remarks"
            v-model="eventCER.remarks"
            :required="need_more_infos"
            :disabled="permission"
          />
        </div>
        <SelectEventTimeslotModal
          v-if="
            metadata.events.formInputs.display_timeslots.visible.type.includes(
              eventCER.type
            ) && eventCER.event_kind_id
          "
          :asset="eventCER.asset_id"
          :type="eventCER.type"
          :event-kind="eventCER.event_kind_id"
          :options="eventCER.timeslots"
          :index="work.events.indexOf(eventCER)"
          v-model="selectedEventTimeslot"
          @open="fetchTimeslots"
        />
      </template>
      <!--#endregion -->

      <!--#region else -->
      <template v-else>
        <BaseRadioCards
          v-if="
            metadata &&
            (auth_role === 'crema-user' || auth_role === 'super-admin')
          "
          v-model="data_room"
          :label="$t('attributes.ceremony_place')"
          :options="superUserRoomSelectOption"
          :disabled="permission"
        />
        <BaseRadioCards
          v-if="auth_role === 'customer-user' && metadata.events"
          v-model="customer_user_room"
          :label="$t('global.room')"
          :options="toOptions(qualifiedRoomsSettingsEventFalse)"
          :disabled="permission"
        />
        <BaseRadioCards
          v-if="eventCER.asset_id"
          name="event_kind_id"
          :label="$t('attributes.ceremony_type')"
          :model-value="eventCER.event_kind_id"
          :options="ceremonyKindOptions"
          :disabled="permission"
          @change="getInputValue"
        />
        <SelectEventTimeslotModal
          v-if="
            metadata.events.formInputs.display_timeslots.visible.type.includes(
              eventCER.type
            ) &&
            eventCER.event_kind_id &&
            auth_role !== 'customer-user'
          "
          :asset="eventCER.asset_id"
          :type="eventCER.type"
          :event-kind="eventCER.event_kind_id"
          :options="eventCER.timeslots"
          :index="work.events.indexOf(eventCER)"
          v-model="selectedEventTimeslot"
          @open="fetchTimeslots"
        />
      </template>
      <!--#endregion -->
    </div>
  </PageSection>
</template>

<script>
import CeremonyDisponibilitiesRadio from './CeremonyDisponibilitiesRadio'

import SelectEventTimeslotModal from '@p/components/Event/SelectEventTimeslotModal.vue'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import moment from 'moment'

export default {
  name: 'SelectCeremonyForm',
  components: {
    CeremonyDisponibilitiesRadio,
    SelectEventTimeslotModal
  },
  data() {
    return {
      ceremony: null,
      data_room: null,
      customer_user_room: null,
      yes_no_options: { true: true, false: false },
      need_more_infos: false,
      selectedEventTimeslot: null,
      today: null,
      date_to_fetch: null
    }
  },
  computed: {
    ...mapGetters({
      work: 'work/getWork',
      metadata: 'work/getWorkMetadata',
      events: 'work/getWorkEvents',
      auth: 'auth/getAuth',
      settings: 'settings/getSystemSettings'
    }),

    qualifiedRoomOptions() {
      return (this.metadata?.events?.assets ?? []).filter(
        (asset) =>
          asset.type === 'ROM' && asset.nb_places >= this.eventCER.nb_peoples
      )
    },
    ceremonyKindOptions() {
      return (this.metadata?.event_kinds ?? []).filter((item) =>
        this.selectedAsset?.event_kind.includes(item.id)
      )
    },
    selectedAsset() {
      return this.metadata.events.assets.find(
        (asset) => asset.id === this.eventCER.asset_id
      )
    },
    permission() {
      return !this.$can('manage_work')
    },
    eventCER() {
      return (
        this.events && this.events.filter((event) => event.type === 'CER')[0]
      )
    },
    auth_role() {
      return this.auth.roles[0]
    },
    qualifiedRooms() {
      let rooms = this.metadata.events.assets.filter(
        (asset) =>
          asset.type === 'ROM' && asset.nb_places >= this.eventCER.nb_peoples
      )
      let qualifiedRooms = {}
      for (let i = 0; i < rooms.length; i++) {
        qualifiedRooms[rooms[i].id] = rooms[i].name
      }
      return qualifiedRooms
    },
    qualifiedRoomsSettingsEventFalse() {
      let rooms = this.metadata.events.assets.filter(
        (asset) => asset.type === 'ROM'
      )
      let qualifiedRooms = {}
      for (let i = 0; i < rooms.length; i++) {
        qualifiedRooms[rooms[i].id] = rooms[i].name
      }
      return qualifiedRooms
    },
    superUserRoomSelectOption() {
      return this.metadata?.events?.assets?.filter(
        (asset) => asset.type === 'ROM'
      )
    }
  },
  methods: {
    toOptions(rooms) {
      return Object.entries(rooms ?? {}).map(([id, name]) => ({ id, name }))
    },
    ...mapActions({
      fetchEventDisponbilitiesByOption: 'fetchEventDisponbilitiesByOption'
    }),
    ...mapMutations({
      alterWorkEventValuesByEventType: 'work/alterWorkEventValuesByEventType',
      removeEventFromWork: 'work/removeEventFromWork',
      removeEventKeyByEventType: 'work/removeEventKeyByEventType'
    }),
    getCeremonyValue({ value }) {
      this.ceremony = value
      if (value === 'false') {
        this.data_room = null
        this.customer_user_room = null
      }
    },
    getInputValue({ attribute, value }) {
      if (attribute === 'event_kind_id') {
        this.removeEventKeyByEventType({
          type: 'CER',
          key_to_delete: 'remarks'
        })
      }
      if (attribute === 'nb_peoples') {
      }
      this.alterWorkEventValuesByEventType({
        type: 'CER',
        attribute: attribute,
        value: value
      })
    },
    fetchTimeslots() {
      this.fetchEventDisponbilitiesByOption({
        params: {
          asset_id: this.eventCER.asset_id,
          type: this.eventCER.type,
          week_from: moment().locale('fr').week(),
          year: moment().format('YYYY'),
          event_kind_id: this.eventCER.event_kind_id
            ? this.eventCER.event_kind_id
            : null
        },
        index: this.work.events.indexOf(this.eventCER)
      })
    }
  },
  watch: {
    ceremony(newValue) {
      if (newValue === 'true') {
        this.alterWorkEventValuesByEventType({
          type: 'CER',
          attribute: 'type',
          value: 'CER'
        })
      } else {
        this.removeEventFromWork({ type: 'CER' })
      }
    },
    customer_user_room(newValue) {
      this.alterWorkEventValuesByEventType({
        type: 'CER',
        attribute: 'asset_id',
        value: parseInt(newValue)
      })
    },
    data_room(newValue) {
      this.alterWorkEventValuesByEventType({
        type: 'CER',
        attribute: 'asset_id',
        value: parseInt(newValue)
      })
    },
    selectedEventTimeslot(newValue, oldValue) {
      if (newValue !== oldValue && newValue !== null) {
        this.alterWorkEventValuesByEventType({
          type: 'CER',
          attribute: 'date_from',
          value: newValue.string_date
        })
        this.alterWorkEventValuesByEventType({
          type: 'CER',
          attribute: 'date_to',
          value: newValue.string_date
        })
        this.alterWorkEventValuesByEventType({
          type: 'CER',
          attribute: 'time_from',
          value: newValue.string_from
        })
        this.alterWorkEventValuesByEventType({
          type: 'CER',
          attribute: 'time_to',
          value: newValue.string_to
        })
        this.alterWorkEventValuesByEventType({
          type: 'CER',
          attribute: 'asset_id',
          value: newValue.asset.id
        })
      }
    }
  }
}
</script>

<style scoped></style>
